/**
 * State for the file tree and preview.
 *
 * One hook pair serves all three hosts — the Files page, the chat drawer, and
 * (later) the account Files tab — because the only thing that differs between
 * them is the optional threadId, which decides whether /work and /attachments
 * are mounted at all.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  api,
  type VfsFileContent,
  type VfsFileEntry,
  type VfsMountInfo,
  type VfsSyncSource,
} from '../shared/api-client';

export interface UseVfsTreeResult {
  mounts: VfsMountInfo[];
  /** Frozen mirrors within these mounts, keyed by folder path. */
  syncSources: VfsSyncSource[];
  /** Children by directory path. Populated as folders are expanded. */
  childrenOf: Record<string, VfsFileEntry[]>;
  expanded: Set<string>;
  loading: boolean;
  error: string | null;
  toggle: (path: string) => void;
  /** Re-read one directory — after an upload, a delete or a new folder. */
  refresh: (path: string) => Promise<void>;
  /** Re-read every directory currently open, for a full-tree refresh. */
  refreshAll: () => Promise<void>;
}

export function useVfsTree(threadId?: string, accountId?: string): UseVfsTreeResult {
  const [mounts, setMounts] = useState<VfsMountInfo[]>([]);
  const [syncSources, setSyncSources] = useState<VfsSyncSource[]>([]);
  const [childrenOf, setChildrenOf] = useState<Record<string, VfsFileEntry[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (path?: string) => {
    const res = await api.listVfsFiles({ path, threadId, accountId });
    if (!res.success) {
      setError(res.error.message);
      return;
    }
    setError(null);
    if (path === undefined) {
      setMounts(res.data.mounts);
      setSyncSources(res.data.syncSources);
    } else {
      setChildrenOf((prev) => ({ ...prev, [path]: res.data.entries }));
    }
  }, [threadId, accountId]);

  // Mounts first: the tree's roots are whatever this caller actually has, which
  // is not knowable up front — a chat has /work, a dashboard visitor does not.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void api.listVfsFiles({ threadId, accountId }).then((res) => {
      if (cancelled) return;
      if (res.success) { setMounts(res.data.mounts); setSyncSources(res.data.syncSources); setError(null); }
      else setError(res.error.message);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [threadId, accountId]);

  const toggle = useCallback((path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
        // Fetch on first open rather than up front: a tree that loaded every
        // directory eagerly would read the whole filesystem to draw one row.
        void load(path);
      }
      return next;
    });
  }, [load]);

  const refresh = useCallback(async (path: string) => { await load(path); }, [load]);

  const refreshAll = useCallback(async () => {
    await Promise.all([...expanded].map((p) => load(p)));
  }, [expanded, load]);

  return { mounts, syncSources, childrenOf, expanded, loading, error, toggle, refresh, refreshAll };
}

export interface UseVfsFileResult {
  file: VfsFileContent | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  /** Save edited text back. Resolves with `{ ok, conflict }` so the preview can
   *  offer Reload/Overwrite rather than a bare error string. `force` writes
   *  unconditionally (expectedHash = null), which is the Overwrite path. */
  save: (text: string, opts?: { force?: boolean }) => Promise<{ ok: boolean; conflict: boolean }>;
  reload: () => Promise<void>;
}

export function useVfsFile(path: string | null, threadId?: string, accountId?: string): UseVfsFileResult {
  const [file, setFile] = useState<VfsFileContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (target: string) => {
    setLoading(true);
    setError(null);
    const res = await api.getVfsFileContent({ path: target, threadId, accountId });
    setLoading(false);
    if (res.success) setFile(res.data);
    else { setFile(null); setError(res.error.message); }
  }, [threadId, accountId]);

  useEffect(() => {
    if (path) void load(path);
    else { setFile(null); setError(null); }
  }, [path, load]);

  const save = useCallback(async (text: string, opts: { force?: boolean } = {}): Promise<{ ok: boolean; conflict: boolean }> => {
    if (!file) return { ok: false, conflict: false };
    setSaving(true);
    // Compare-and-set on the hash we loaded, unless the caller forced an
    // overwrite: a stale save is reported as a conflict rather than silently
    // winning, and Overwrite is the explicit way past it.
    const expectedHash = opts.force === true ? null : (file.contentHash || null);
    const res = await api.saveVfsFileContent({
      path: file.path, content: text, threadId, accountId, expectedHash,
    });
    setSaving(false);
    if (!res.success) {
      setError(res.error.message);
      // The conflict code is what distinguishes "someone else wrote first" from a
      // real failure, so the preview can offer a Reload/Overwrite choice.
      return { ok: false, conflict: res.error.code === 'CONFLICT' };
    }
    setFile({ ...file, text, contentHash: res.data.contentHash });
    setError(null);
    return { ok: true, conflict: false };
  }, [file, threadId, accountId]);

  return {
    file,
    loading,
    saving,
    error,
    save,
    reload: async () => { if (path) await load(path); },
  };
}
